import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Popover } from "react-bootstrap";
import ContentEditable from "react-contenteditable";
import ScrollToBottom from "react-scroll-to-bottom";
import Picker from "@emoji-mart/react";
import { UserHover } from "Components/UserHover";
import { VideoRecorder } from "./VideoRecorder";
import { authService, newAuthService } from "Service/Axios";
import { REPLY_COMMENT_URL, UPLOAD_TASK_URL } from "Constant/api";
import { useChatList, useUserList } from "Constant/apiHooks";
import {
  CHATS_FILE_TYPES,
  DEFAULT_MODULE_TYPE,
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_DOC_ONLY,
  DOCUMENT_TYPE_PDF_ONLY,
  DOCUMENT_TYPE_PPT_ONLY,
  DOCUMENT_TYPE_XLS_ONLY,
  IMAGE_TYPES,
} from "Constant/constants";
import { useOutsideClick } from "Utils/hooks";
import {
  formatDateWithFullMonthTime,
  getFirstLetterOfName,
  handleFilterListBySearch,
  uploadHandler,
} from "Utils";

export const Chat = ({ gid }) => {
  const { t } = useTranslation();
  const commentRef = useRef(null);
  const searchRef = useRef(null);
  const emojiRef = useRef(null);

  const [isSearch, setIsSearch] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [videoRecorderModal, setVideoRecorderModal] = useState(false);
  const [replyId, setReplyId] = useState("");
  const [commentContent, setCommentContent] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [commentAttachmentList, setCommentAttachmentList] = useState([]);
  const [commentVideoList, setCommentVideoList] = useState([]);

  const { userList: defaultUserList } = useUserList();
  const {
    chatList,
    // loading: chatLoading,
    setChatList,
    fetchChatList,
  } = useChatList(gid);

  useOutsideClick(searchRef, () => setIsSearch(false));
  useOutsideClick(emojiRef, () => setShowEmoji(false));

  // Toggle
  const showEmojiDropdown = () => {
    setShowEmoji(!showEmoji);
  };

  const toggleMoreChat = (index, value) => {
    const newArr = [...chatList];
    newArr[index].showMoreChat = value;
    setChatList(newArr);
  };

  const toggleVideoRecorderModal = () => {
    setVideoRecorderModal(!videoRecorderModal);
  };

  useEffect(() => {
    if (commentRef.current) {
      commentRef.current.focus();
    }
  }, []);

  // Listing
  const userList = useMemo(() => {
    if (searchUser?.length > 0) {
      const response = handleFilterListBySearch(
        defaultUserList,
        "fullName",
        searchUser
      );
      return response;
    }
    return defaultUserList;
  }, [defaultUserList, searchUser]);

  // Utils
  const setCursorToNextLine = () => {
    const contentEditableNode = commentRef.current;
    contentEditableNode.innerHTML += "<br>";

    const range = document.createRange();
    range.setStart(
      contentEditableNode.childNodes[contentEditableNode.childNodes.length - 1],
      0
    );
    range.setEnd(
      contentEditableNode.childNodes[contentEditableNode.childNodes.length - 1],
      0
    );

    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);
    contentEditableNode.focus();
  };

  // On Change
  const handleChange = (e) => {
    if (e.target.name === "searchUser") {
      setSearchUser(e.target.value);
    }
  };

  const handleChangeComment = (e) => {
    setCommentContent(e.target.value);

    const inputValue = e.target.value;

    // Check if "@" is present in the input
    const atIndex = inputValue.lastIndexOf("@");
    if (atIndex !== -1) {
      const typedAfterAt = inputValue.substring(atIndex + 1);

      // Check if typed characters after "@" are valid (a-z)
      const validInput = /^[a-zA-Z\s]*$/;
      if (typedAfterAt.match(validInput)) {
        setIsSearch(true);
        setSearchUser(typedAfterAt);
      } else {
        setIsSearch(false);
        setSearchUser("");
      }
    } else {
      setIsSearch(false);
      setSearchUser("");
    }
  };

  const handleKeyDownComment = (e) => {
    const contentEditableElement = commentRef.current;
    const selection = window.getSelection();
    const anchorElement = selection.anchorNode;

    if (
      e.key === "Backspace" &&
      anchorElement &&
      anchorElement.parentNode.nodeName === "A"
    ) {
      const parentAnchorElement = anchorElement.parentNode;
      parentAnchorElement.parentNode.removeChild(parentAnchorElement);
      const newHtml = contentEditableElement.innerHTML;
      setCommentContent(newHtml);
      e.preventDefault();
    }
    if (
      e.key === "Backspace" &&
      anchorElement &&
      anchorElement.parentNode.nodeName === "SPAN"
    ) {
      const parentAnchorElement = anchorElement.parentNode;
      parentAnchorElement.parentNode.removeChild(parentAnchorElement);
      const newHtml = contentEditableElement.innerHTML;
      setCommentContent(newHtml);
      e.preventDefault();
    }
    // 1 @ por space bar dile hbe na
    // 2 @p
    // 3 @p por abr jodi space thake thahole oke allow korte hbe
    // Check if the pressed key is "@" and handle suggestion
    // if (e.key === "@") {
    //   setIsSearch(true);
    // }
  };

  const handlePasteComment = (evt) => {
    evt.preventDefault();
    const text = evt.clipboardData.getData("text/plain");
    const youtubeRegex =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const vimeoRegex = /https?:\/\/vimeo\.com\/(\d+)/;
    if (youtubeRegex.test(text)) {
      const youtubeId = text.match(youtubeRegex)[1];
      const embedHTML = `<a href="${text}" target="_blank">${text}</a><div style="position:relative; padding-bottom:40.25%; height:0; overflow:hidden; width:100%;"><iframe src="https://www.youtube.com/embed/${youtubeId}" style="position:absolute; top:0; left:0; width:100%; height:100%;" frameborder="0" allowfullscreen></iframe></div><br>`;
      document.execCommand("insertHTML", false, embedHTML);
    } else if (vimeoRegex.test(text)) {
      const vimeoId = text.match(vimeoRegex)[1];
      const embedHTML = `<a href="${text}" target="_blank">${text}</a><div style="position:relative; padding-bottom:40.25%; height:0; overflow:hidden; width:100%;"><iframe src="https://player.vimeo.com/video/${vimeoId}" style="position:absolute; top:0; left:0; width:100%; height:100%;" frameborder="0" allowfullscreen></iframe></div><br>`;
      document.execCommand("insertHTML", false, embedHTML);
    } else {
      document.execCommand("insertText", false, text);
    }
  };

  const handleAddEmoji = (emoji) => {
    setCommentContent(commentContent + emoji.native);
  };

  const handleTagUser = (user) => {
    // const url = user.userId;
    const contentEditableElement = commentRef.current;
    const selection = window.getSelection();
    // const range = selection.getRangeAt(0);

    // Find the position of the last "@" in the text
    const atIndex = commentContent.lastIndexOf("@");
    if (atIndex !== -1) {
      // Get the text after the last "@" symbol
      // const textAfterAt = commentContent.substring(atIndex);

      // Remove the "@" symbol and keep the rest of the text
      // const newText = textAfterAt.replace(
      //   /^@/,
      //   `<a href="/user/${user.userId}">${user.firstName} ${user.lastName}</a>`
      // );

      // Replace the original text with the new text
      const newHtml =
        commentContent.substring(0, atIndex) +
        `
          <span class="hover-effect position-relative">
              <span class="hover-show d-flex align-items-center">
                <span class="w-12 h-12 flex-auto me-2 d-flex justify-content-center align-items-center text-white rounded-circle bg-primary-200 overflow-hidden">
                  ${user.photoURL
          ? `<span class="img-box w-100 h-100 d-block">
                      <img
                        width="24"
                        height="24"
                        src="${user.photoURL}"
                        alt="${user.firstName}${user.lastName}"
                      />
                    </span>`
          : `
                        <div
                          class="short-name d-flex justify-content-center align-items-center text-uppercase w-100 h-100"
                          style="background-color: ${user.profileColor ? user.profileColor : "#182A58"
          }"
                        >
                          ${user.firstName.charAt(0)} ${user.lastName.charAt(0)}
                        </div>
                      `
        }
                </span>
                <a
                  href=${user.email}
                  id=${user.userId}
                  class="text-grey-800 text-decoration-none text-sm font-medium w-100 text-nowrap"
                >
                ${user.firstName} ${user.lastName}
                </a>
              </span>

              <span class="hover-box p-3 rounded-2 bg-white position-absolute bottom-100 start-0 shadow z-3">
                <span class="new-avater d-flex">
                  <span class="mb-2 position-relative">
                    <span class="me-2 w-24 h-24 d-block rounded-circle bg-grey-100 position-relative overflow-hidden">
                      ${user.photoURL
          ? `<img
                            src="${user.photoURL}"
                            alt="${user.firstName}${user.lastName}"
                            class="w-100 h-100 object-fit-cover"
                          />`
          : `
                            <div
                              class="short-name d-flex justify-content-center align-items-center text-uppercase w-100 h-100"
                              style="background-color: ${user.profileColor
            ? user.profileColor
            : "#182A58"
          }"
                            >
                              ${user.firstName.charAt(
            0
          )} ${user.lastName.charAt(0)}
                            </div>
                          `
        }
                    </span>
                  </span>

                  <span class="new-av-details">
                    <h4 class="mb-0 text-sm font-semibold">
                      ${user.firstName} ${user.lastName}
                    </h4>

                    <ul class="m-0 p-0 list-unstyled text-xs d-flex flex-column row-gap-0">
                      <li>
                        <i class="bx bx-user"></i> ${user.type}
                      </li>
                      <li>
                        <i class="bx bx-envelope"></i> ${user.email}
                      </li>
                      <li>
                        <i class="bx bx-time"></i> 4:30 local time
                      </li>
                    </ul>

                    <span class="d-flex gap-1 mt-3">
                      <a
                        href="/"
                        class="btn btn-mini btn-block btn-light text-nowrap"
                      >
                        View Profile
                      </a>
                      <button
                        type="button"
                        class="btn btn-mini btn-block btn-dark text-nowrap"
                      >
                        Send Message
                      </button>
                    </span>
                  </span>
                </span>
              </span>
            </span>
          ` +
        "<br>";

      contentEditableElement.innerHTML = newHtml;

      // Update selection range
      const newRange = document.createRange();
      newRange.selectNodeContents(contentEditableElement);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);

      // Update comment content
      setCommentContent(newHtml);
      contentEditableElement.addEventListener("keydown", handleKeyDownComment);
      setIsSearch(false);
    }
  };

  const handleUploadDocument = async (e) => {
    const response = await uploadHandler(
      e,
      DEFAULT_MODULE_TYPE.chat,
      CHATS_FILE_TYPES,
      UPLOAD_TASK_URL,
      [gid]
    );
    if (response) {
      const { name: fileName, filetype: fileType } = response;
      if (IMAGE_TYPES.includes(fileType)) {
        const image = `<br /><img src=${response.url} alt=${fileName} /><br />`;
        const newComment = commentContent + image;
        setCommentContent(newComment);
        setCursorToNextLine();
      }
      if (DOCUMENT_TYPES.includes(fileType)) {
        setCommentAttachmentList((prev) => [...prev, response]);
        // const doc = `<br />
        // 	<span class="file-format">
        // 		<div class="format-icon">
        // 			<i class="bx bxs-file-pdf"></i>
        // 		</div>
        // 		<div class="file-name-des">
        // 			<span class="file-title">
        // 				${fileName}
        // 			</span>
        // 			<span class="file-size">
        // 				${fileSize}
        // 			</span>
        // 		</div>
        // 	</span>
        // <br />
        // `;
        // const newComment = commentContent
        //   .substring(0, commentContent.length - 1)
        //   .concat("", doc);
        // setCommentContent(newComment);
      }
      e.target.value = "";
    }
  };

  const handleUploadVideo = async (url) => {
    setCommentVideoList((prev) => [...prev, url]);
  };

  // const handlePlayVideo = async (url) => {
  //   // Open a new tab/window
  //   const newWindow = window.open("", "_blank");
  //   newWindow.document.write(
  //     `<html><body><video controls autoplay><source src="${url}" type="video/mp4"></video></body></html>`
  //   );
  //   // Close the document to render the content
  //   newWindow.document.close();
  // };

  const handleRemoveVideo = async (url) => {
    // Remove the video URL from the list
    const updatedVideoList = commentVideoList.filter((video) => video !== url);

    // Update the state with the new video list
    setCommentVideoList(updatedVideoList);
  };

  // Submit & Reset
  const onReplyComment = (chat) => {
    setReplyId(chat.gid);
    handleTagUser(chat);
  };

  const onSendComment = async () => {
    // For Email User
    const container = document.createElement("div");
    container.innerHTML = commentContent;
    const anchorTags = container.querySelectorAll("a[id]"); // Select all anchor tags with href attribute
    const taggedUserForEmail = [];
    anchorTags.forEach((anchor) => {
      const user = userList?.find((user) => user.userId === anchor.id);
      taggedUserForEmail.push(`
        <tr>
          <td align="left" class="responsive-padding" style="padding: 20px;">
            <table border="0" cellpadding="0" cellspacing="0" width="100%" class="responsive-table">
              <tbody>
                <tr>
                  <td style="padding:10px; border: 1px solid #ddd;font-size: 14px;">
                    <table class="hover-effect position-relative">
                      <thead>
                        <tr>
                          <th align="left" width="48" style="padding:2px 10px 2px 0">
                            ${user.photoURL
          ? `
                          <td class="img-box w-100 h-100 d-block">
                            <img src="${user.photoURL}" alt="${user.firstName}${user.lastName}"
                              style="background-color:red; width:100%;height:100%;object-fit:cover" />
                          </td>`
          : `
                          <td class="short-name d-flex justify-content-center align-items-center text-uppercase w-100 h-100"
                            style="background-color: ${user.profileColor ? user.profileColor : " #182A58"
          }">
                            ${user.firstName.charAt(0)} ${user.lastName.charAt(
            0
          )}
                          </td>
                          `
        }
                          </th>
                          <th align="left" style="padding:8px 0">
                            <h6 style="margin:0;font-size:16px;font-weight:normal;">${user.firstName
        } ${user.lastName}</h6>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colspan="2" height="10"></td>
                        </tr>
                        <tr>
                          <td colspan="2">
                            <p style="margin:0;font-size: 14px;">: In literary theory, a text is any object that can be "read",
                              whether this object is a work of literature, a street sign, an arrangement of buildings on a city
                              block</p>
                          </td>
                        </tr>
                      </tbody>


                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>`);
    });

    const payload = {
      chatDetails: commentContent,
      taskId: gid,
      htmlChat: taggedUserForEmail,
    };
    if (replyId) {
      payload.replyId = replyId;
    }
    if (commentAttachmentList && commentAttachmentList.length > 0) {
      payload.attachment = commentAttachmentList;
    }
    if (commentVideoList && commentVideoList.length > 0) {
      payload.videos = commentVideoList;
    }
    await newAuthService(REPLY_COMMENT_URL, "POST", payload);
    setCommentContent("");
    setCommentAttachmentList([]);
    setCommentVideoList([]);
    setReplyId("");
    setIsSearch(false);
    fetchChatList(gid);
  };

  const onUpdateLike = async (chat, type) => {
    const payload = {
      gid: chat.gid,
      taskId: gid,
    };
    if (type === "like") {
      if (!chat.like) {
        payload.like = true;
      } else {
        payload.unlike = true;
      }
    }
    if (type === "dislike") {
      if (!chat.dislike) {
        payload.dislike = true;
      } else {
        payload.undislike = true;
      }
    }
    await authService(REPLY_COMMENT_URL, "POST", payload);
    fetchChatList(gid);
  };

  // Render
  const documentIconRender = (attachment) => {
    if (DOCUMENT_TYPE_PDF_ONLY.includes(attachment.filetype)) {
      return <img src="/assets/images/icons/pdf.svg" alt="pdf" />;
    } else if (DOCUMENT_TYPE_DOC_ONLY.includes(attachment.filetype)) {
      return <img src="/assets/images/icons/docx.svg" alt="docx" />;
    } else if (DOCUMENT_TYPE_PPT_ONLY.includes(attachment.filetype)) {
      return <img src="/assets/images/icons/pptx.svg" alt="pptx" />;
    } else if (DOCUMENT_TYPE_XLS_ONLY.includes(attachment.filetype)) {
      return <img src="/assets/images/icons/xlsx.svg" alt="xlsx" />;
    } else {
      return <img src="" alt="invalid" />;
    }
  };

  return (
    <div className="applicant-status-body comments-tab">
      <div className="comments-wrapper">
        <div className="comments-body px-5">
          <ScrollToBottom>
            <ul className="comments-list">
              {chatList &&
                chatList.length > 0 &&
                chatList.map((chat, index) => (
                  <li key={chat.gid}>
                    <div className="child-avatar flex-wrap">
                      <div className="d-flex w-100 mb-2">
                        <div className="cursor-pointer">
                          {/* <div className="d-flex align-items-center">
                            <div className="avatar-img">
                              {chat.photoURL ? (
                                <div className="img-box">
                                  <img
                                    src={chat.photoURL}
                                    alt={`${chat.firstName}${chat.lastName}`}
                                  />
                                </div>
                              ) : (
                                getFirstLetterOfName(chat)
                              )}
                            </div>
                            <h4 className="mb-0 text-sm text-nowrap">
                              {chat.firstName} {chat.lastName}
                            </h4>
                          </div> */}

                          {/* <div className="hover-box rounded-2 position-absolute top-100 start-0 shadow z-3">
                            <UserHover user={chat} />
                          </div> */}

                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Popover className="max-w-unset">
                                <UserHover user={chat} />
                              </Popover>
                            }
                          >
                            <div className="d-flex align-items-center">
                              <div className="w-8 h-8 rounded-circle border border-grey-200 overflow-hidden">
                                {chat.photoURL ? (
                                  <div className="img-box w-100 h-100">
                                    <img
                                      src={chat.photoURL}
                                      alt={`${chat.firstName}${chat.lastName}`}
                                      className="w-100 h-100 object-fit-cover"
                                    />
                                  </div>
                                ) : (
                                  getFirstLetterOfName(chat)
                                )}
                              </div>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="avatar-short-dtls ms-2">
                                    <h4 className="mb-0 text-sm text-nowrap">
                                      {chat.firstName} {chat.lastName}
                                    </h4>
                                  </div>
                                  <span className="text-grey-300 font-light">|</span>
                                  <div>
                                    <smal className="text-xs font-medium text-grey-400">42 mins ago</smal>
                                  </div>
                                </div>
                            </div>
                          </OverlayTrigger>
                        </div>
                        <div className="comments-body-nav">
                          <ul className="d-flex">
                            <li>
                              <span className="comments-time">
                                {chat.createdOn &&
                                  formatDateWithFullMonthTime(chat.createdOn)}
                              </span>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="true"
                                data-bs-auto-close="outside"
                              >
                                <i className="bx bx-dots-horizontal-rounded"></i>
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end text-center border-grey-200 bg-grey-50">
                                <li className="border-bottom border-grey-200">
                                  <button type="button" className="py-1 px-3 d-flex align-items-center gap-1 w-100">
                                    <i className="bx bx-pin"></i> Pin
                                  </button>
                                </li>
                                <li className="border-bottom border-grey-200">
                                  <button type="button" className="py-1 px-3 d-flex align-items-center gap-1 w-100">
                                    <i className="bx bx-message-square-dots"></i> Replay
                                  </button>
                                </li>
                                <li>
                                  <button type="button" className="py-1 px-3 d-flex align-items-center gap-1 w-100 bounded">
                                    <i className="bx bx-trash"></i> Remove
                                  </button>
                                </li>
                              </ul>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="avatar-comment">
                        <div
                          className="text-sm"
                          dangerouslySetInnerHTML={{
                            __html: chat.chatDetails,
                          }}
                        />
                        {chat.attachment?.length > 0 &&
                          chat.attachment.map((attachment, attachmentIndex) => (
                            <span
                              className="file-format d-flex-inline align-items-center ps-1 pe-2 py-1"
                              key={attachmentIndex.toString()}
                            >
                              <div className="format-icon">
                                {documentIconRender(attachment)}
                              </div>
                              <div className="file-name-des d-flex flex-column gap-1">
                                <a
                                  className="file-title"
                                  href={attachment.url}
                                  target="_blank"
                                >
                                  {attachment.name}
                                </a>
                                <span className="file-size">
                                  {attachment.size}
                                </span>
                              </div>
                            </span>
                          ))}
                        {chat.videos?.length > 0 &&
                          chat.videos.map((video, videoIndex) => (
                            <span
                              className="video-format d-flex align-items-center p-1 position-relative"
                              key={videoIndex.toString()}
                            >
                              <a
                                className="w-8 h-8 bx-md d-flex justify-content-center align-items-center text-white border-0 bg-transparent position-absolute video-play-btn z-2"
                                href={video}
                                target="_blank"
                              >
                                <i className="bx bx-play"></i>
                              </a>
                              <video
                                className="video-player"
                                src={video}
                                controls
                              ></video>
                            </span>
                          ))}
                        <ul className="react-list">
                          <li>
                            <button
                              type="button"
                              className="thumb-btn thumb-like"
                              onClick={() => onUpdateLike(chat, "like")}
                            >
                              <img
                                src="/assets/images/icons/thumb-icon.svg"
                                alt=""
                              />{" "}
                              {chat.likesno}
                            </button>
                            <button
                              type="button"
                              className="thumb-btn thumb-dislike"
                              onClick={() => onUpdateLike(chat, "dislike")}
                            >
                              <img
                                src="/assets/images/icons/thumb-icon.svg"
                                alt=""
                              />{" "}
                              {chat.dislikesno}
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              onClick={() => onReplyComment(chat)}
                            >
                              {t("common.button.reply")}
                            </button>
                            <span className="replay-tag">
                              {chat.repliesno} {t("common.button.replies")}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    {chat.replies && chat.replies.length > 0 && (
                      <>
                        {chat.showMoreChat ? (
                          <ul className="sub-comments-list">
                            {chat.replies.map((subchat) => (
                              <li>
                                <div className="child-avatar">
                                  <div className="d-flex w-100 mb-2">
                                    <div className="hover-effect position-relative cursor-pointer">
                                      <div class="d-flex align-items-center">
                                        <div className="avatar-img">
                                          {subchat.photoURL ? (
                                            <div className="img-box">
                                              <img
                                                src={subchat.photoURL}
                                                alt={`${subchat.firstName}${subchat.lastName}`}
                                              />
                                            </div>
                                          ) : (
                                            getFirstLetterOfName(subchat)
                                          )}
                                        </div>
                                        <h4 className="mb-0 text-sm text-nowrap">
                                          {subchat.firstName} {subchat.lastName}
                                        </h4>
                                      </div>

                                      <div className="hover-box rounded-2 position-absolute top-100 start-0 shadow z-3">
                                        <UserHover user={subchat} />
                                      </div>
                                    </div>
                                    <div className="comments-body-nav">
                                      <ul>
                                        <li>
                                          <span className="comments-time">
                                            {subchat.createdOn &&
                                              formatDateWithFullMonthTime(
                                                subchat.createdOn
                                              )}
                                          </span>
                                        </li>
                                        <li>
                                          <button
                                            type="button"
                                            className="dropdown-toggle"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                          >
                                            <i className="bx bx-dots-horizontal-rounded"></i>
                                          </button>
                                          <ul className="dropdown-menu dropdown-menu-end">
                                            <li>
                                              <button type="button">
                                                <i className="bx bx-star"></i>
                                              </button>
                                            </li>
                                            <li>
                                              <button type="button">
                                                <i className="bx bx-pin"></i>
                                              </button>
                                            </li>
                                            <li>
                                              <button type="button">
                                                <i className="bx bx-box"></i>
                                              </button>
                                            </li>
                                          </ul>
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                  <div className="avatar-comment">
                                    <div
                                      className="text-sm"
                                      dangerouslySetInnerHTML={{
                                        __html: subchat.chatDetails,
                                      }}
                                    />
                                    {subchat.attachment?.length > 0 &&
                                      subchat.attachment.map(
                                        (
                                          attachment,
                                          subchatAttachmentIndex
                                        ) => (
                                          <span
                                            className="file-format"
                                            key={subchatAttachmentIndex.toString()}
                                          >
                                            <div className="format-icon">
                                              {documentIconRender(attachment)}
                                            </div>
                                            <div className="file-name-des d-flex flex-column gap-1">
                                              <span className="file-title">
                                                {attachment.name}
                                              </span>
                                              <span className="file-size">
                                                {attachment.size}
                                              </span>
                                            </div>
                                          </span>
                                        )
                                      )}
                                    <ul className="react-list">
                                      <li>
                                        <button
                                          type="button"
                                          className="thumb-btn thumb-like"
                                          onClick={() =>
                                            onUpdateLike(subchat, "like")
                                          }
                                        >
                                          <img
                                            src="/assets/images/icons/thumb-icon.svg"
                                            alt=""
                                          />{" "}
                                          {subchat.likesno}
                                        </button>
                                        <button
                                          type="button"
                                          className="thumb-btn thumb-dislike"
                                          onClick={() =>
                                            onUpdateLike(subchat, "dislike")
                                          }
                                        >
                                          <img
                                            src="/assets/images/icons/thumb-icon.svg"
                                            alt=""
                                          />{" "}
                                          {subchat.dislikesno}
                                        </button>
                                      </li>
                                      {/* <li>
                                        <button type="button">Reply</button>
                                        <span className="replay-tag">
                                          {subchat.repliesno}{" "}
                                          {t("common.button.replies")}
                                        </span>
                                      </li> */}
                                    </ul>
                                  </div>
                                </div>
                              </li>
                            ))}
                            <button
                              type="button"
                              className="hide-replay"
                              onClick={() => toggleMoreChat(index, false)}
                            >
                              {t("common.button.hideReplies")}
                            </button>
                          </ul>
                        ) : (
                          <button
                            type="button"
                            className="last-prev-replay"
                            onClick={() => toggleMoreChat(index, true)}
                          >
                            {t("common.button.loadPreviousReplies")}
                          </button>
                        )}
                      </>
                    )}
                  </li>
                ))}
            </ul>
          </ScrollToBottom>
        </div>
        <div className="comments-footer">
          <div className="editor-box">
            <div className="text-editor p-3 rounded-lg border border-grey-200 bg-grey-50">
              <ContentEditable
                innerRef={commentRef}
                html={commentContent}
                disabled={false}
                onChange={handleChangeComment}
                onKeyDown={handleKeyDownComment}
                onPaste={handlePasteComment}
              />
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {commentAttachmentList?.length > 0 &&
                  commentAttachmentList.map((attachment, index) => (
                    <span
                      className="file-format d-flex align-items-center ps-1 pe-2 py-2"
                      key={index.toString()}
                    >
                      <div className="format-icon">
                        {documentIconRender(attachment)}
                      </div>
                      <div className="file-name-des d-flex flex-column row-gap">
                        <a
                          className="file-title"
                          href={attachment.url}
                          target="_blank"
                        >
                          {attachment.name}
                        </a>
                        <span className="file-size">{attachment.size}</span>
                      </div>
                    </span>
                  ))}
                {commentVideoList?.length > 0 &&
                  commentVideoList.map((video, index) => (
                    <span
                      className="d-flex align-items-center p-1 position-relative"
                      key={index.toString()}
                    >
                      <button
                        className="w-5 h-5 d-flex justify-content-center align-items-center rounded-circle text-white border-2 border-solid border-slate-50 bg-slate-600 position-absolute z-1 video-close-btn"
                        onClick={() => handleRemoveVideo(video)}
                      >
                        <i className="bx bx-x"></i>
                      </button>

                      <a
                        className="w-8 h-8 bx-md d-flex justify-content-center align-items-center text-white border-0 bg-transparent position-absolute video-play-btn z-2"
                        // onClick={() => handlePlayVideo(video)}
                        href={video}
                        target="_blank"
                      >
                        <i className="bx bx-play"></i>
                      </a>
                      <video
                        className="video-player"
                        src={video}
                        controls
                      ></video>
                    </span>
                  ))}
              </div>
            </div>
            {isSearch && (
              <div className="search-category" ref={searchRef}>
                {/* <div className="search-div p-2">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="pluto-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("common.placeHolder.searchApplicant")}
                      name="searchUser"
                      value={searchUser}
                      onChange={handleChange}
                    />
                  </div>
                </div> */}
                <ul className="m-0 p-0">
                  {userList && userList.length > 0 ? (
                    userList.map((user) => (
                      <li key={user.userId}>
                        <button onClick={() => handleTagUser(user)}>
                          <div className="d-flex align-items-center">
                            <div className="tag-user me-2">
                              {user.photoURL ? (
                                <img
                                  src={user.photoURL}
                                  alt={`${user.firstName}${user.lastName}`}
                                />
                              ) : (
                                getFirstLetterOfName(user)
                              )}
                              <div className="short-name">S A</div>
                            </div>
                            {user.firstName} {user.lastName}
                          </div>
                        </button>
                      </li>
                    ))
                  ) : (
                    <li>
                      <button>
                        <div className="d-flex align-items-center">
                          {t("common.messages.error.notFound.notFoundUser")}
                        </div>
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}
            <div className="editor-tools-bar flex-wrap">
              <div className="editor-tools-icons">
                <ul>
                  {/* <li>
                    <button type="button" onClick={showSuggestion}>
                      <i className="bx bx-at"></i>
                    </button>
                  </li> */}
                  <li className="emoji" ref={emojiRef}>
                    <button type="button" onClick={showEmojiDropdown}>
                      <i className="bx bx-smile"></i>
                    </button>
                    {showEmoji && (
                      <Picker set="google" onEmojiSelect={handleAddEmoji} />
                    )}
                  </li>
                  <li>
                    <button type="button" onClick={toggleVideoRecorderModal}>
                      <i className="bx bx-video"></i>
                    </button>
                  </li>
                  <li>
                    <input
                      type="file"
                      id="file"
                      onChange={handleUploadDocument}
                    />
                    <label htmlFor="file" className="px-1">
                      <i className="bx bx-paperclip"></i>
                    </label>
                  </li>
                  {/* <li>
                    <button type="button">
                      <i className="bx bx-font-color"></i>
                    </button>
                  </li> */}
                </ul>
              </div>
              <div className="send-btn-box">
                <button
                  type="submit"
                  className="btn send-btn text-uppercase"
                  onClick={onSendComment}
                >
                  {t("common.button.send")} <i className="bx bxs-send"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {videoRecorderModal && (
        <VideoRecorder
          show={videoRecorderModal}
          toggle={toggleVideoRecorderModal}
          handleSave={handleUploadVideo}
        />
      )}
    </div>
  );
};
